// Import react elements.
import { useContext, useEffect, useState } from "react";

// Import API calls.
import { getFaqs } from "api/APIStrichCalls";

// Import context function.
import { AuthContext } from "global/context/AuthContext";

// Import hooks.
import usePageTitle from "global/hooks/usePageTitle";

// Import components.
import { LoadingSpinner } from "global/components/loading_ui/LoadingSpinnerAnimation";

// Import css stylesheet.
import "./SupportPage.css";



const SupportPage = () => {

    // Set the page title.
    usePageTitle("Support");

    // Get the authenticated API token.
    const { token } = useContext(AuthContext);

    // Store the FAQ sections returned by the API.
    const [faqSections, setFaqSections] = useState([]);

    // Track the FAQ loading state.
    const [loading, setLoading] = useState(true);

    // Track which FAQ sections have been expanded.
    const [expandedSections, setExpandedSections] = useState({});



    // Expand or collapse a FAQ section.
    const toggleSection = (sectionKey) => {

        setExpandedSections((currentSections) => ({
            ...currentSections,
            [sectionKey]: !currentSections[sectionKey],
        }));

    };



    // Load the FAQs when authentication is available.
    useEffect(() => {

        // Stop until a token is available.
        if (!token) { return; }

        // Load all published FAQs.
        const loadFaqs = async () => {

            // Mark the FAQ content as loading.
            setLoading(true);

            // Request the FAQ sections.
            const response = await getFaqs(token);

            // Store the returned FAQ sections when the request succeeded.
            if (response?.success) {
                setFaqSections(Array.isArray(response.results) ? response.results : []);
            }

            // Mark the FAQ content as loaded.
            setLoading(false);

        };

        // Run the FAQ request.
        loadFaqs();

    }, [token]);



    // Render the support page.
    return (

        <div className="support-page">

            {/* Render the page heading. */}
            <div className="support-page__heading">
                <h1>Support</h1>
                <p>Frequently asked questions about StrichSuite.</p>
            </div>

            {/* Show a loading state while the FAQs are being requested. */}
            {loading ? (

                <div className="support-page__loading">
                    <LoadingSpinner message="Loading FAQs..." />
                </div>

            ) : (

                // Render all returned FAQ sections.
                <div className="support-page__sections">

                    {/* Loop through each FAQ section. */}
                    {faqSections.map((section, sectionIndex) => {

                        // Prepare the section display state.
                        const sectionKey = section?.id ?? sectionIndex;
                        const sectionFaqs = Array.isArray(section?.faqs) ? section.faqs : [];
                        const sectionExpanded = expandedSections[sectionKey] === true;
                        const visibleFaqs = sectionExpanded ? sectionFaqs : sectionFaqs.slice(0, 3);
                        const hiddenFaqCount = sectionFaqs.length - 3;

                        // Render the section and its FAQs.
                        return (

                            <section
                                className="support-page__section"
                                key={sectionKey}
                            >

                                {/* Render the section heading. */}
                                <div className="support-page__section-heading">

                                    <h2>{section?.name}</h2>

                                    {/* Render the section description when one has been supplied. */}
                                    {section?.description && (
                                        <p>{section.description}</p>
                                    )}

                                </div>

                                {/* Render the visible FAQs in this section. */}
                                <div className="support-page__faqs">

                                    {/* Loop through each visible FAQ in the section. */}
                                    {visibleFaqs.map((faq, faqIndex) => (

                                        // Render an expandable FAQ.
                                        <details
                                            className="support-page__faq"
                                            key={faq?.id ?? faqIndex}
                                        >

                                            {/* Render the FAQ question. */}
                                            <summary className="support-page__question">
                                                {faq?.question}
                                            </summary>

                                            {/* Render the trusted FAQ answer HTML. */}
                                            <div
                                                className="support-page__answer"
                                                dangerouslySetInnerHTML={{ __html: faq?.answer || "" }}
                                            />

                                        </details>

                                    ))}

                                </div>

                                {/* Allow sections with more than three FAQs to be expanded. */}
                                {hiddenFaqCount > 0 && (
                                    <button
                                        className="support-page__more"
                                        type="button"
                                        onClick={() => toggleSection(sectionKey)}
                                        aria-expanded={sectionExpanded}
                                    >
                                        {sectionExpanded ? "Show less" : `Show ${hiddenFaqCount} more`}
                                    </button>
                                )}

                            </section>

                        );

                    })}

                </div>

            )}

            {/* Keep direct support available below the FAQs. */}
            <p className="support-page__contact">
                Still need help? <a href="mailto:support@strichsuite.com">Contact us.</a>
            </p>

        </div>

    );

};

export default SupportPage;